import React from 'react';
import {
  Box,
  Heading,
  Text,
  Badge,
  Flex,
  Divider,
  SimpleGrid,
  VStack,
  HStack,
  Icon,
  Alert,
  AlertIcon,
  Code,
} from '@chakra-ui/react';
import { FiMessageSquare } from 'react-icons/fi';

const ToolCard = ({ tool }) => (
  <Box
    p={5}
    bg="white"
    borderRadius="xl"
    boxShadow="sm"
    borderWidth="1px"
    borderColor="gray.100"
    transition="all 0.2s"
    _hover={{ boxShadow: 'md', transform: 'translateY(-2px)' }}
  >
    <Code
      display="inline-block"
      px={2}
      py={1}
      borderRadius="md"
      bg="purple.50"
      color="purple.700"
      fontSize="sm"
      fontWeight="semibold"
      mb={2}
    >
      {tool.name}
    </Code>
    <Text fontSize="sm" color="gray.600" mb={3}>
      {tool.description}
    </Text>
    <VStack align="stretch" spacing={2}>
      {tool.examples.map((example, i) => (
        <HStack
          key={i}
          bg="gray.50"
          borderRadius="md"
          px={3}
          py={2}
          spacing={2}
          align="start"
        >
          <Icon as={FiMessageSquare} color="teal.400" boxSize={3.5} mt={0.5} flexShrink={0} />
          <Text fontSize="sm" color="gray.700" fontStyle="italic">"{example}"</Text>
        </HStack>
      ))}
    </VStack>
  </Box>
);

const TOOL_GROUPS = [
  {
    title: 'Events & Capacity',
    tools: [
      {
        name: 'get_upcoming_events',
        description: 'Events starting in a date window, with registration counts.',
        examples: ['What events do I have tomorrow?', "What's happening this week?", 'What do we have on in the next 2 months?'],
      },
      {
        name: 'get_registration_counts',
        description: 'Signup count for one event.',
        examples: ['How many people have signed up for the Annual Gala?'],
      },
      {
        name: 'get_rundown_gap_events',
        description: "Events whose Event Planner rundown doesn't yet fill the scheduled duration.",
        examples: ['Which events this week still have gaps in their rundown?'],
      },
      {
        name: 'get_waitlist_status',
        description: 'Waitlist count for an event, broken down by priority tier.',
        examples: ['How many people are on the waitlist for the Annual Gala?', 'Is the Summit full?'],
      },
      {
        name: 'get_event_hosts_and_venue',
        description: 'Venue details and assigned hosts for an event.',
        examples: ["Where is the Annual Gala being held, and who's hosting it?"],
      },
    ],
  },
  {
    title: 'Ticketing & Payments',
    tools: [
      {
        name: 'get_ticket_revenue',
        description: 'Ticket sales and revenue for a ticketed event.',
        examples: ['How much ticket revenue has the Annual Gala made?', 'How many tickets have we sold for the Summit?'],
      },
      {
        name: 'get_coupon_usage',
        description: 'Redemption stats for a discount coupon.',
        examples: ['How many times has the SUMMER25 coupon been used?'],
      },
      {
        name: 'get_cart_activity',
        description: 'Shopping cart activity in a window — a signal for checkout abandonment.',
        examples: ['How many abandoned checkouts did we have this week?'],
      },
      {
        name: 'get_profile_credits',
        description: 'Credit balance and transaction history for one profile (requires a numeric profile id).',
        examples: ["What's the credit balance for profile 482?"],
      },
    ],
  },
  {
    title: 'Courses',
    tools: [
      {
        name: 'get_upcoming_course_sessions',
        description: 'Course sessions starting in a date window, with enrollment counts.',
        examples: ['What course sessions are running next month?'],
      },
      {
        name: 'get_course_enrollment_counts',
        description: 'Enrollment count for a course session.',
        examples: ['How many people are enrolled in the Python course?'],
      },
      {
        name: 'get_course_catalog',
        description: 'Search the course catalog by title.',
        examples: ['What courses do we offer on leadership?', 'List our courses.'],
      },
    ],
  },
  {
    title: 'Support & Community',
    tools: [
      {
        name: 'get_enquiry_backlog',
        description: 'Open/in-progress/resolved support enquiry counts, by status and topic.',
        examples: ['How many open enquiries do we have?', 'What are people enquiring about most?'],
      },
      {
        name: 'get_group_activity',
        description: 'Membership count and recent discussion activity for a community group.',
        examples: ['How active is the Marketing Circle group?', 'How many members does the Finance Group have?'],
      },
    ],
  },
  {
    title: 'Communications',
    tools: [
      {
        name: 'get_broadcast_stats',
        description: 'Delivery stats for broadcast email campaigns.',
        examples: ['How many people did the August Newsletter reach?'],
      },
      {
        name: 'get_email_backlog',
        description: 'Count of email threads with unread inbound messages awaiting a reply.',
        examples: ['How many unread emails are we sitting on?'],
      },
      {
        name: 'get_sms_activity',
        description: 'SMS send counts in a date window.',
        examples: ['How many SMS messages did we send this month?'],
      },
    ],
  },
  {
    title: 'Members',
    tools: [
      {
        name: 'get_membership_overview',
        description: 'Total/active/inactive/enrolled membership counts, by type and region.',
        examples: ['How many active members do we have?', 'Break down our members by region.'],
      },
      {
        name: 'get_profile_segment_counts',
        description: 'Profile counts per tag/segment.',
        examples: ['How many profiles are tagged VIP?'],
      },
      {
        name: 'get_referral_stats',
        description: 'Total referral counts and the top referring profiles.',
        examples: ["How many referrals have we had, and who's referring the most?"],
      },
    ],
  },
];

const TOTAL_TOOLS = TOOL_GROUPS.reduce((sum, g) => sum + g.tools.length, 0);

const Tools = () => (
  <Box>
    <Box mb={8}>
      <Heading size="lg" color="gray.800" mb={2}>Assistant Tools</Heading>
      <Text color="gray.500">
        Everything the chat assistant can look up — {TOTAL_TOOLS} read-only tools, all scoped to the
        selected instance. The same tools are also exposed to external MCP clients (see the MCP Server
        section of the <Code fontSize="sm">API Documentation</Code> page); this page is the plain-language
        version for the Chat UI.
      </Text>
    </Box>

    <Alert status="info" borderRadius="lg" mb={8}>
      <AlertIcon />
      <Text fontSize="sm">
        These are examples, not exact phrasing the assistant requires — it's a language model, not a
        command parser. Ask naturally; it picks the right tool(s) itself, and can chain several together
        to answer one question.
      </Text>
    </Alert>

    {TOOL_GROUPS.map((group, i) => (
      <Box key={group.title} mb={8}>
        <Flex align="center" mb={4}>
          <Heading size="md" color="gray.700">{group.title}</Heading>
          <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
            {group.tools.length} tool{group.tools.length !== 1 ? 's' : ''}
          </Badge>
        </Flex>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          {group.tools.map((tool) => (
            <ToolCard key={tool.name} tool={tool} />
          ))}
        </SimpleGrid>
        {i < TOOL_GROUPS.length - 1 && <Divider mt={8} />}
      </Box>
    ))}
  </Box>
);

export default Tools;
